import { useState, type PropsWithChildren } from 'react';
import { useMessage } from '@assistant-ui/react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';
import ReasoningTimeline from './ReasoningTimeline';
import { useTransparencySteps } from './TransparencyStepsContext';
import type { TransparencyStep } from '../../types/transparency';

// ChatReasoningTrace — collapsible reasoning trace
//
// Reads transparency steps from TransparencyStepsContext (a side-channel
// provided by SkillStreamRuntimeProvider / DurableStreamRuntimeProvider).
// Renders retrieval / generation / transform steps via ReasoningTimeline.
// Tool steps are filtered out — the per-turn "Turn N: tool selection"
// generation steps already cover the same ground in a friendlier form.

export default function ChatReasoningTrace() {
  const [expanded, setExpanded] = useState(false);

  const messageId = useMessage((m) => m.id);
  const steps = useTransparencySteps(messageId);
  const traceData = steps && steps.length > 0 ? { steps } : null;

  const isRunning = useMessage((m) => m.status?.type === 'running');

  if (!traceData?.steps || traceData.steps.length === 0) return null;

  const nonToolSteps: TransparencyStep[] = traceData.steps.filter((s) => s.type !== 'tool');

  if (nonToolSteps.length === 0) return null;

  // Auto-expand while streaming (steps are arriving), collapse when done
  const showExpanded = expanded || isRunning;

  return (
    <div className="mt-2 rounded-lg border border-border/50 dark:border-nightshade-700/50 bg-muted/30 dark:bg-nightshade-800/30">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60 dark:text-nightshade-400/60 hover:text-muted-foreground dark:hover:text-nightshade-200 transition-colors"
      >
        <ChevronRight
          className={cn('h-3 w-3 transition-transform', showExpanded && 'rotate-90')}
        />
        Reasoning
        <span className="text-muted-foreground/40 dark:text-nightshade-500/40">
          · {nonToolSteps.length} steps
        </span>
        {isRunning && (
          <span className="ml-auto animate-pulse text-content-action dark:text-blueberry-400">●</span>
        )}
      </button>
      {showExpanded && (
        <div className="px-3 pb-2">
          <ReasoningTimeline steps={nonToolSteps} variant="compact" />
        </div>
      )}
    </div>
  );
}

// ChatReasoningGroup — wrapper for ReasoningGroup in MessagePrimitive.Parts
//
// The library groups consecutive `reasoning` content parts and renders them
// inside a ReasoningGroup component. We use this wrapper to hide the
// individual reasoning text (the ReasoningTimeline handles display via the
// data part) and show nothing here — ChatReasoningTrace handles the UI.

export function ChatReasoningGroup({
  children: _children,
}: PropsWithChildren<{ startIndex: number; endIndex: number }>) {
  // Individual reasoning parts are hidden — ChatReasoningTrace reads
  // the structured data part and renders the full timeline.
  return null;
}
